import { useEffect, useState } from 'react';

/**
 * Custom hook to get the screen size and determine the device type based on the screen width.
 * @function useScreenSize
 * @returns {object} An object containing the screen size and device type.
 * @returns {object} return.screenSize - The current screen size.
 * @returns {number} return.screenSize.width - The current screen width.
 * @returns {number} return.screenSize.height - The current screen height.
 * @returns {boolean} return.isMobile - Whether the device is a mobile device.
 * @returns {boolean} return.isTablet - Whether the device is a tablet.
 * @returns {boolean} return.isDesktop - Whether the device is a desktop.
 * @returns {boolean} return.isUltrawide - Whether the device is an ultrawide screen.
 */
export const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window?.innerWidth : 0,
    height: typeof window !== 'undefined' ? window?.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window?.innerWidth || 0,
        height: window?.innerHeight || 0,
      });
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    screenSize: screenSize,
    isMobile: screenSize.width < 768,
    isTablet: screenSize.width >= 768 && screenSize.width < 992,
    isDesktop: screenSize.width >= 992 && screenSize.width < 1500,
    isUltrawide: screenSize.width >= 1500,
  };
};
